import React, { useState, useEffect, useRef } from 'react';

// Monochrome grayscale ramp — matches AnalyticsPanel palette
const METRICS = [
  { key: 'total_visits',    name: 'Visitas',    color: '#0A0A0A', fill: 'rgba(10,10,10,0.06)'    },
  { key: 'unique_visitors', name: 'Únicos',     color: '#525252', fill: 'rgba(82,82,82,0.06)'    },
  { key: 'social_clicks',   name: 'Clics',      color: '#8C8C8C', fill: 'rgba(140,140,140,0.06)' },
  { key: 'vcf_downloads',   name: 'Contactos',  color: '#C2C2C2', fill: 'rgba(194,194,194,0.06)' },
];

const PERIOD_LABELS = {
  '7d': 'Últimos 7 días',
  '1m': 'Último mes',
  '3m': 'Últimos 3 meses',
  '6m': 'Últimos 6 meses',
  '12m': 'Últimos 12 meses',
  'all-time': 'Todo el tiempo',
};

const EnhancedLineChart = ({ data, period, timeUnit }) => {
  const [tooltip, setTooltip] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [dim, setDim] = useState({ width: 600, height: 280 });
  const containerRef = useRef(null);

  // Driven ENTIRELY by the container's own width — never window.innerWidth.
  // The chart lives inside a dashboard card whose available width depends
  // on the sidebar's collapsed/expanded state and the 12-col grid's
  // current column span, neither of which tracks viewport width 1:1
  // (e.g. a collapsed sidebar frees ~160px at the exact same window
  // width). Sizing off `vw` while measuring `w` is what previously let
  // the SVG render narrower than its own container — up to ~160px of
  // dead space on the right of the card at common tablet/laptop widths —
  // since the two could disagree about which size bucket applied.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      setDim({
        width: Math.max(w, 220),
        height: w <= 340 ? 210 : w <= 620 ? 260 : 300,
      });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    // Kept alongside ResizeObserver: belt-and-suspenders for the rare
    // browser/environment where ResizeObserver misses a layout pass.
    window.addEventListener('resize', update);
    return () => { ro.disconnect(); window.removeEventListener('resize', update); };
  }, []);

  // Right padding only needs to clear the rightmost point's hover ring
  // (r=5) — nothing else is ever drawn in that margin (no axis, no
  // labels), so it stays small instead of matching the left margin's
  // label-driven width.
  const pad = dim.width <= 340
    ? { top: 12, right: 12, bottom: 36, left: 34 }
    : dim.width <= 620
    ? { top: 16, right: 14, bottom: 40, left: 42 }
    : { top: 20, right: 16, bottom: 44, left: 50 };

  const cw = dim.width  - pad.left - pad.right;
  const ch = dim.height - pad.top  - pad.bottom;

  const allValues = METRICS.flatMap(m => data.map(d => d[m.key] || 0));
  const maxVal = Math.max(...allValues, 10);
  const minVal = Math.min(...allValues, 0);
  const range  = maxVal - minVal || 1;

  const ys = v  => ch - ((v - minVal) / range) * ch;
  const xs = i  => (i / Math.max(data.length - 1, 1)) * cw;

  // Catmull-Rom style smooth path
  const makePath = (metric) => {
    const pts = data.map((d, i) => ({ x: xs(i), y: ys(d[metric.key] || 0) }));
    if (!pts.length) return '';
    if (pts.length === 1) return `M${pts[0].x},${pts[0].y}`;
    let d = `M${pts[0].x},${pts[0].y}`;
    for (let i = 1; i < pts.length; i++) {
      const p = pts[i - 1], c = pts[i], n = pts[i + 1];
      if (i === pts.length - 1) { d += ` L${c.x},${c.y}`; continue; }
      const cp1x = p.x + (c.x - p.x) * 0.45;
      const cp2x = c.x - (n.x - c.x) * 0.25;
      d += ` C${cp1x},${p.y} ${cp2x},${c.y} ${c.x},${c.y}`;
    }
    return d;
  };

  const makeArea = (metric) => {
    const path = makePath(metric);
    if (!path) return '';
    return `${path} L${xs(data.length - 1)},${ch} L${xs(0)},${ch} Z`;
  };

  const tickCount = dim.width <= 340 ? 4 : 5;
  const yTicks = Array.from({ length: tickCount }, (_, i) =>
    minVal + range * (i / (tickCount - 1))
  );

  // Derived from the actual plot width in pixels, not a fixed fraction of
  // data.length — a fixed /4 or /7 divisor has no idea how wide a label
  // actually renders, so the same divisor that looks fine for 7 points
  // packs full "YYYY-MM-DD" labels into ~70px slots once data.length
  // grows for 12m/all-time views, running consecutive labels together.
  // minLabelPx is the minimum center-to-center spacing a label (plus
  // breathing room) needs to never touch its neighbor.
  const minLabelPx = dim.width <= 340 ? 46 : 58;
  const maxLabels = Math.max(1, Math.floor(cw / minLabelPx));
  const labelStep = Math.max(1, Math.ceil((data.length || 1) / maxLabels));

  // Same idea, much denser: how many data-point MARKERS actually fit
  // without overlapping each other. With a year of daily points (365) on
  // a narrow phone-width chart (~250px of plot area), drawing a circle at
  // every single point packs them so tightly the dots fuse into a solid
  // band that hides the line underneath — markers are a hover/tap
  // affordance, not required at every point, so thin them to roughly one
  // every 9px and always keep the very last point (the most recent value
  // is the one most worth being able to tap).
  const maxPoints = Math.max(1, Math.floor(cw / 9));
  const pointStep = Math.max(1, Math.ceil((data.length || 1) / maxPoints));
  const showPoint = (i) => i % pointStep === 0 || i === data.length - 1;

  const fmtLabel = (d) => {
    if (!d.period) return '';
    // Always abbreviated to "MM-DD" — the full year adds little when
    // scanning a trend line (hovering/tapping a point already shows the
    // exact date in the tooltip) and is what was actually colliding.
    if (timeUnit === 'day') return d.period.slice(5);
    return d.period.split(' ')[0].slice(0, 3);
  };

  const fmtNum = (n) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : Math.round(n).toString();

  const svgFont = "'Inter', system-ui, sans-serif";

  return (
    <div ref={containerRef} style={{ width: '100%', overflowX: 'hidden', position: 'relative' }}>

      {/* ── SVG Chart ─────────────────────────────────────────────── */}
      <svg
        width={dim.width}
        height={dim.height}
        viewBox={`0 0 ${dim.width} ${dim.height}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', maxWidth: '100%' }}
      >
        <defs>
          {METRICS.map(m => (
            <linearGradient key={m.key} id={`grad-${m.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={m.color} stopOpacity="0.18"/>
              <stop offset="100%" stopColor={m.color} stopOpacity="0"/>
            </linearGradient>
          ))}
        </defs>

        {/* Horizontal grid lines only — clean Apple style */}
        {yTicks.map((val, i) => (
          <line
            key={i}
            x1={pad.left}
            y1={ys(val) + pad.top}
            x2={dim.width - pad.right}
            y2={ys(val) + pad.top}
            stroke="rgba(0,0,0,0.06)"
            strokeWidth="1"
          />
        ))}

        {/* X baseline */}
        <line
          x1={pad.left}
          y1={dim.height - pad.bottom}
          x2={dim.width - pad.right}
          y2={dim.height - pad.bottom}
          stroke="rgba(0,0,0,0.1)"
          strokeWidth="1"
        />

        {/* Y-axis tick labels */}
        {yTicks.map((val, i) => (
          <text
            key={i}
            x={pad.left - 8}
            y={ys(val) + pad.top + 4}
            textAnchor="end"
            fontSize={dim.width <= 340 ? 10 : 11}
            fill="#aeaeb2"
            fontFamily={svgFont}
          >
            {fmtNum(val)}
          </text>
        ))}

        {/* X-axis labels */}
        {data.map((d, i) => i % labelStep === 0 && (
          <text
            key={i}
            x={xs(i) + pad.left}
            y={dim.height - pad.bottom + 14}
            textAnchor="middle"
            fontSize={dim.width <= 340 ? 10 : 11}
            fill="#aeaeb2"
            fontFamily={svgFont}
          >
            {fmtLabel(d)}
          </text>
        ))}

        {/* Chart group */}
        <g transform={`translate(${pad.left},${pad.top})`}>
          {METRICS.map(m => (
            <g key={m.key}>
              {/* Area gradient fill */}
              <path
                d={makeArea(m)}
                fill={`url(#grad-${m.key})`}
              />
              {/* Line */}
              <path
                d={makePath(m)}
                fill="none"
                stroke={m.color}
                strokeWidth={dim.width <= 340 ? 1.5 : 2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* Data points — thinned via showPoint() on dense ranges, see above */}
              {data.map((d, i) => {
                if (!showPoint(i)) return null;
                const isHov = hovered === `${i}-${m.key}`;
                const r = isHov ? 5 : (dim.width <= 340 ? 2.5 : 3.5);
                const showTooltipFor = (clientX, clientY, currentTarget) => {
                  // Tooltip is a plain absolutely-positioned HTML div sharing
                  // the container's CSS pixel box — no SVG viewBox scaling
                  // involved, so this stays a direct client-rect offset.
                  const svgEl = currentTarget.closest('svg');
                  const rect = svgEl.getBoundingClientRect();
                  setTooltip({
                    x: clientX - rect.left,
                    y: clientY - rect.top,
                    period: d.period || `Período ${i + 1}`,
                    metric: m.name,
                    value: d[m.key] || 0,
                    color: m.color,
                  });
                  setHovered(`${i}-${m.key}`);
                };
                return (
                  <g key={i}>
                    {/* Visible marker — purely decorative, never the hit
                        target itself (its radius is too small to tap
                        reliably on a touchscreen). */}
                    <circle
                      cx={xs(i)}
                      cy={ys(d[m.key] || 0)}
                      r={r}
                      fill="#fff"
                      stroke={m.color}
                      strokeWidth={isHov ? 2.5 : 1.5}
                      style={{ transition: 'r 0.15s' }}
                      pointerEvents="none"
                    />
                    {/* Invisible, much larger hit target — same center,
                        r=14 is close to the ~44px/22px-radius touch target
                        size guidelines, so a finger can land near (not
                        exactly on) the visible point and still register. */}
                    <circle
                      cx={xs(i)}
                      cy={ys(d[m.key] || 0)}
                      r={14}
                      fill="transparent"
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={(e) => showTooltipFor(e.clientX, e.clientY, e.currentTarget)}
                      onMouseLeave={() => { setTooltip(null); setHovered(null); }}
                      onTouchStart={(e) => {
                        e.preventDefault();
                        const t = e.touches[0];
                        if (t) showTooltipFor(t.clientX, t.clientY, e.currentTarget);
                      }}
                      onTouchEnd={() => { setTooltip(null); setHovered(null); }}
                    />
                  </g>
                );
              })}
            </g>
          ))}
        </g>
      </svg>

      {/* ── Legend ──────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px 20px',
        marginTop: 14,
        padding: '10px 14px',
        background: 'rgba(0,0,0,0.025)',
        borderRadius: 10,
        border: '1px solid rgba(0,0,0,0.05)',
      }}>
        {METRICS.map(m => (
          <div key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: m.color,
              flexShrink: 0,
            }}/>
            <div style={{ width: 16, height: 2, borderRadius: 99, background: m.color, flexShrink: 0 }}/>
            <span style={{
              fontSize: 12,
              fontWeight: 500,
              color: '#48484a',
              fontFamily: "'Inter', system-ui, sans-serif",
              letterSpacing: '-0.01em',
            }}>
              {m.name}
            </span>
          </div>
        ))}
      </div>

      {/* ── Tooltip ─────────────────────────────────────────────────── */}
      {tooltip && (() => {
        // Clamped so the tooltip never gets cut off by the container's
        // own overflowX:hidden — previously, hovering/tapping a point
        // near the right edge (common on the narrow mobile chart, since
        // the rightmost points sit closest to it) pushed the tooltip's
        // left edge past dim.width, clipping the text; near the top edge
        // the fixed "-48" offset could push it above the chart entirely.
        const TOOLTIP_W = 140;
        const left = Math.min(tooltip.x + 12, Math.max(dim.width - TOOLTIP_W - 4, 4));
        const top = Math.max(tooltip.y - 48, 4);
        return (
        <div style={{
          position: 'absolute',
          left,
          top,
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'saturate(180%) blur(16px)',
          border: '1px solid rgba(0,0,0,0.1)',
          borderRadius: 12,
          padding: '8px 12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
          pointerEvents: 'none',
          zIndex: 30,
          minWidth: 130,
          maxWidth: TOOLTIP_W,
        }}>
          <div style={{
            fontSize: 11,
            color: '#6e6e73',
            marginBottom: 3,
            fontFamily: "'Inter', system-ui, sans-serif",
            letterSpacing: '-0.01em',
          }}>
            {tooltip.period}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: tooltip.color, flexShrink: 0 }}/>
            <span style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#1d1d1f',
              fontFamily: "'Inter', system-ui, sans-serif",
              letterSpacing: '-0.02em',
            }}>
              {tooltip.metric}: {tooltip.value}
            </span>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default EnhancedLineChart;
